import { Domain, User, UserRole } from "@lr-notitia/notitia-operator/schemas";
import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import { LanguageCode } from "iso-639-1";

import { useInstanceWorkspacesStore } from "./i-workspace";

let instanceWorkspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
function init() {
	instanceWorkspacesStore = useInstanceWorkspacesStore();
}

export const useDomainStore = defineStore("domain", () => {
	let domainId = "";
	const notFound = ref(true);
	const domain = ref<Domain>({} as Domain);
	const account = ref<User>({} as User);
	const members = ref<User[]>([]);
	const authToken = ref<string>("");
	const languages = ref<LanguageCode[]>([]);
	const languagePreference = ref<LanguageCode>(initLanguagePreference());
	const allDomains = ref<Domain[]>([]);

	const isAdmin = computed(() => account.value.role === UserRole.Admin);
	const isEditorOrAdmin = computed(() => account.value.role === UserRole.Editor || isAdmin.value);

	function initLanguagePreference(): LanguageCode {
		return getLocalStorageLanguage() && getLocalStorageLanguage() !== ""
			? (getLocalStorageLanguage() as LanguageCode)
			: "de";
	}

	function loadDomain(dId: string): Promise<Domain> {
		domainId = dId;
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.get()
				.then((data) => {
					domain.value = data;
					languagePreference.value = initLanguagePreference();
					languages.value = data.languages;
					notFound.value = false;
					loadAccount(dId)
						.then(() => loadMembers(dId))
						.then(() => resolve(data));
				})
				.catch((error) => {
					reject(error);
					console.warn("Failed to load domain");
				});
		});
	}

	function loadAllDomains(): Promise<Domain[]> {
		return new Promise((resolve) => {
			api()
				.domains()
				.get()
				.then((data) => {
					allDomains.value = data;
					resolve(data);
				});
		});
	}

	function clearDomain() {
		notFound.value = true;
		domain.value = {} as Domain;
		account.value = {} as User;
		members.value = [];
		domainId = "";
	}

	function loadAccount(dId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.domain(dId)
				.account()
				.get()
				.then((data) => {
					account.value = data;
					resolve();
				})
				.catch((error) => {
					console.warn("Failed to load Account");
					reject(error);
				});
		});
	}

	function refreshMembers(dId: string) {
		api()
			.domain(dId)
			.members()
			.refresh()
			.catch(() => console.warn("Failed to refresh Members"));
	}

	function loadMembers(dId: string) {
		api()
			.domain(dId)
			.members()
			.get()
			.then((data) => (members.value = data))
			.catch(() => console.warn("Failed to load Members"));
	}

	function searchMember(query: string): Promise<User[]> {
		return new Promise((resolve) => {
			api()
				.domain(domainId)
				.searchUsers()
				.get(query)
				.then((data) => resolve(data))
				.catch(() => resolve([]));
		});
	}

	async function addMember(user: User, role: UserRole) {
		await api()
			.domain(domainId)
			.members()
			.post({ userId: user.id, role })
			.then((d) => (members.value = d))
			.catch(() => console.warn("Failed to add member"));
	}

	function moveMember(user: User, role: UserRole) {
		if (!canModify(role)) return;
		api()
			.domain(domainId)
			.members()
			.del({ userId: user.id })
			.then(() => {
				api()
					.domain(domainId)
					.members()
					.post({ userId: user.id, role })
					.then((d) => (members.value = d))
					.catch(() => console.warn("Failed to move member"));
			})
			.catch(() => console.warn("Failed to move member"));
		return;
	}

	function removeMember(userId: string) {
		api()
			.domain(domainId)
			.members()
			.del({ userId })
			.then(() => {
				members.value = members.value.filter((m) => m.id !== userId);
			})
			.catch(() => console.warn("Failed to remove member"));
	}

	function canModify(targetUserRole: UserRole) {
		let modifyableRoles: string[] = [];
		switch (account.value.role) {
			case UserRole.Admin:
				modifyableRoles = [UserRole.Admin, UserRole.Editor, UserRole.Viewer];
				break;
			case UserRole.Editor:
				modifyableRoles = [UserRole.Viewer];
				break;
			case UserRole.Viewer:
				modifyableRoles = [];
				break;
		}
		if (modifyableRoles.includes(targetUserRole)) return true;
		else return false;
	}

	function hasAccessRights(neededRole: UserRole): boolean {
		if (neededRole === UserRole.Admin) return isAdmin.value;
		else if (neededRole === UserRole.Editor) return isEditorOrAdmin.value;
		else if (neededRole === UserRole.Viewer) return true;
		return false;
	}

	function getLocalStorageLanguage(): string {
		return localStorage.getItem("languagePreference") || "";
	}
	function setLocalStorageLanguage(lang: LanguageCode) {
		localStorage.setItem("languagePreference", lang);
	}

	watch(
		() => members.value,
		() => {
			members.value.sort((a, b) => {
				if (a.lastname < b.lastname) return -1;
				if (a.lastname > b.lastname) return 1;
				return 0;
			});
		}
	);
	watch(
		() => allDomains.value,
		() => {
			allDomains.value.sort((a, b) => {
				if (a.name < b.name) return -1;
				if (a.name > b.name) return 1;
				return 0;
			});
		}
	);
	watch(
		() => languagePreference.value,
		(newVal) => {
			setLocalStorageLanguage(newVal);
			if (!instanceWorkspacesStore) init();
			// instanceWorkspacesStore.sortAlphabetically(instanceWorkspacesStore.workspaces);
		}
	);

	return {
		notFound,
		domain,
		allDomains,
		account,
		members,
		isAdmin,
		isEditorOrAdmin,
		loadDomain,
		loadAllDomains,
		clearDomain,
		loadAccount,
		refreshMembers,
		loadMembers,
		searchMember,
		addMember,
		moveMember,
		removeMember,
		canModify,
		languages,
		languagePreference,
		hasAccessRights,
		authToken,
	};
});
